import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LogOut, Trash2, Users, Activity, Flame, Building2, Radio, Send, Pencil } from "lucide-react";
import PrincetonDemoCard from "@/pages/admin/PrincetonDemoCard";
import CompGrantCard from "@/pages/admin/CompGrantCard";
import TourAnalyticsCard from "@/pages/admin/TourAnalyticsCard";
import GlobalSoloRosterCard from "@/pages/admin/GlobalSoloRosterCard";
import AdminDataExportsCard from "@/pages/admin/AdminDataExportsCard";
import AdminPayoutQueue from "@/components/AdminPayoutQueue";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [teams, setTeams] = useState([]);
  const [stats, setStats] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get("/admin/teams"),
        api.get("/admin/stats"),
      ]);
      setTeams(t.data || []);
      setStats(s.data || null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPulse = useCallback(async () => {
    try { const r = await api.get("/admin/pulse"); setPulse(r.data); }
    catch (err) { console.warn("[admin] pulse fetch failed:", err); }
  }, []);

  useEffect(() => { load(); loadPulse(); }, [load, loadPulse]);
  useEffect(() => {
    const t = setInterval(loadPulse, 60000);  // 60s auto-refresh
    return () => clearInterval(t);
  }, [loadPulse]);

  const handleLogout = async () => { await logout(); nav("/", { replace: true }); };

  const renameTeam = async (team_id, current) => {
    const name = window.prompt("New team name:", current);
    if (!name || !name.trim() || name.trim() === current) return;
    try {
      await api.patch(`/admin/teams/${team_id}`, { name: name.trim() });
      toast.success(`Renamed to "${name.trim()}"`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteTeam = async (team_id, name) => {
    if (!window.confirm(`Hard-delete team "${name}" and all its users/logs? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/teams/${team_id}`);
      toast.success(`Deleted ${name}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const [digestRunning, setDigestRunning] = useState(false);
  const sendWeeklyDigest = async (dryRun) => {
    if (!dryRun && !window.confirm("Send the Week-Ahead digest email to ALL athletes on EVERY tenant right now?")) return;
    setDigestRunning(true);
    try {
      const r = await api.post(`/admin/weekly-digest?dry_run=${dryRun}`);
      const sent = r.data.results.reduce((s, x) => s + (x.sent || 0), 0);
      const skipped = r.data.results.reduce((s, x) => s + (x.skipped || 0), 0);
      toast.success(dryRun ? `Dry-run OK — ${sent + skipped} forecasts computed (${skipped} skipped)` : `Sent ${sent} emails (${skipped} skipped)`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDigestRunning(false); }
  };

  const STATS_CARDS = [
    { key: "teams",        label: "Teams",          icon: Building2, color: "var(--brand-secondary)" },
    { key: "coaches",      label: "Coaches",        icon: Users,     color: "#22D3EE" },
    { key: "players",      label: "Players",        icon: Users,     color: "#16A34A" },
    { key: "logs_7d",      label: "Logs · 7-day",   icon: Activity,  color: "#F59E0B" },
    { key: "critical_7d",  label: "Critical · 7d",  icon: Flame,     color: "#EF4444" },
  ];

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: "var(--ink-1)" }} data-testid="admin-dashboard">
      <header className="border-b border-[var(--ink-border)] sticky top-0 z-20 backdrop-blur" style={{ backgroundColor: "rgba(11,30,54,0.85)" }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Logo size="md" variant="lockup" linkTo="/admin" dark />
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Platform Admin</div>
              <div className="text-sm text-white/90 font-medium">{user?.name}</div>
            </div>
            <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm" style={{ backgroundColor: "var(--brand-secondary)" }}>
              {(user?.name || "A")[0].toUpperCase()}
            </div>
            <Button variant="outline" onClick={handleLogout} className="border-white/15 text-white/80 bg-white/5 hover:bg-white/10" data-testid="admin-logout">
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">System Console</div>
          <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter mt-1">Global Overview</h1>
          <p className="text-sm text-white/50 mt-2">Every tenant on the ArmIQ platform. Click a row to inspect, or delete to hard-purge a team.</p>
        </div>

        {/* Platform Pulse — live strip, refreshes every 60s */}
        <section className="rounded-xl ink-card overflow-hidden" data-testid="platform-pulse">
          <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--ink-border)] bg-white/[0.02]">
            <div className="flex items-center gap-2">
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ backgroundColor: "#22D3EE" }} />
                <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: "#22D3EE" }} />
              </span>
              <Radio className="h-3.5 w-3.5 text-white/60" />
              <span className="text-[10px] tracking-[0.25em] font-bold text-white/60 uppercase">Platform Pulse</span>
              <span className="text-[10px] text-white/30 ml-2">refreshes every 60s</span>
            </div>
            <span className="text-[10px] font-mono-num text-white/40" data-testid="pulse-timestamp">
              {pulse?.now_iso ? new Date(pulse.now_iso).toLocaleTimeString() : "—"}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-7 divide-x divide-[var(--ink-border)]">
            {[
              { label: "Logs · 1h",       k: "logs_last_hour",     color: "#22D3EE" },
              { label: "Logs · Today",    k: "logs_today",         color: "#22D3EE" },
              { label: "Critical · 1h",   k: "critical_last_hour", color: "#EF4444" },
              { label: "Critical · Today",k: "critical_today",     color: "#EF4444" },
              { label: "Apple Health · 24h", k: "apple_health_24h",  color: "#F59E0B" },
              { label: "Coaches · 24h",   k: "active_coaches_24h", color: "#16A34A" },
              { label: "Players · 24h",   k: "active_players_24h", color: "#16A34A" },
            ].map((it) => (
              <div key={it.k} className="px-4 py-3" data-testid={`pulse-${it.k}`}>
                <div className="text-[10px] tracking-[0.2em] font-bold text-white/40 uppercase">{it.label}</div>
                <div className="font-display font-black text-3xl font-mono-num mt-1" style={{ color: it.color }}>
                  {pulse?.[it.k] ?? "—"}
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {STATS_CARDS.map((s) => {
            const Icon = s.icon;
            const val = stats?.[s.key] ?? "—";
            return (
              <div key={s.key} className="rounded-xl ink-card p-5" data-testid={`admin-stat-${s.key}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">{s.label}</div>
                  <Icon className="h-4 w-4" style={{ color: s.color }} />
                </div>
                <div className="font-display font-black text-4xl font-mono-num mt-2" style={{ color: s.color }}>{val}</div>
              </div>
            );
          })}
        </div>

        <section className="rounded-xl ink-card overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--ink-border)] flex-wrap gap-3">
            <h2 className="font-display font-bold text-lg">All Teams</h2>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" disabled={digestRunning} onClick={() => sendWeeklyDigest(true)} className="border-white/15 text-white bg-white/5 hover:bg-white/10" data-testid="weekly-digest-dryrun">
                <Send className="h-3.5 w-3.5 mr-2" /> Preview week-ahead
              </Button>
              <Button size="sm" disabled={digestRunning} onClick={() => sendWeeklyDigest(false)} className="text-white red-glow font-bold" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="weekly-digest-send">
                <Send className="h-3.5 w-3.5 mr-2" /> Send week-ahead
              </Button>
              <div className="text-xs text-white/40">{teams.length} tenants</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-teams-table">
              <thead className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">
                <tr className="border-b border-[var(--ink-border)]">
                  <th className="text-left px-6 py-3">Team</th>
                  <th className="text-left px-6 py-3">Brand</th>
                  <th className="text-right px-6 py-3">Coaches</th>
                  <th className="text-right px-6 py-3">Players</th>
                  <th className="text-right px-6 py-3">Logs 7d</th>
                  <th className="text-right px-6 py-3">Critical 7d</th>
                  <th className="text-right px-6 py-3">Created</th>
                  <th className="text-right px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td className="px-6 py-6 text-white/40" colSpan={8}>Loading teams…</td></tr>
                ) : teams.length === 0 ? (
                  <tr><td className="px-6 py-6 text-white/40" colSpan={8}>No teams yet.</td></tr>
                ) : teams.map((t) => (
                  <tr key={t.team_id} className="border-b border-[var(--ink-border)] hover:bg-white/[0.02]" data-testid={`admin-team-row-${t.team_id}`}>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-white">{t.name}</div>
                      <div className="text-xs text-white/40 font-mono-num">{t.team_id.slice(0, 8)}…</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        <span className="w-4 h-4 rounded ring-1 ring-white/10" style={{ backgroundColor: t.primary_color }} />
                        <span className="w-4 h-4 rounded ring-1 ring-white/10" style={{ backgroundColor: t.secondary_color }} />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right font-mono-num">{t.coach_count}</td>
                    <td className="px-6 py-4 text-right font-mono-num">{t.player_count}</td>
                    <td className="px-6 py-4 text-right font-mono-num">{t.logs_7d}</td>
                    <td className="px-6 py-4 text-right font-mono-num" style={{ color: t.critical_7d ? "#EF4444" : undefined }}>{t.critical_7d}</td>
                    <td className="px-6 py-4 text-right text-xs text-white/50">{(t.created_at || "").slice(0, 10)}</td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => renameTeam(t.team_id, t.name)}
                        className="text-white/50 hover:text-white hover:bg-white/10"
                        data-testid={`admin-rename-${t.team_id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteTeam(t.team_id, t.name)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`admin-delete-${t.team_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <PrincetonDemoCard />
        <GlobalSoloRosterCard />
        <AdminDataExportsCard />
        <AdminPayoutQueue />
        <TourAnalyticsCard />
        <CompGrantCard teams={teams} onGranted={load} />
      </main>
    </div>
  );
}
